<template>
    <div id="neural-film-ai-processing-modal" class="modal-new hidden">
        <span class="title">{{ neuralFilmAIProcessingModalTitle || 'NeuralFilmAI' }}</span>

        <v-stepper alt-labels dark flat v-model="neuralFilmAIProcessingStep">
            <v-stepper-header>
                <v-stepper-step step="1" :complete="neuralFilmAIProcessingStep > 1">
                    Initializing AI Models
                </v-stepper-step>

                <v-divider></v-divider>

                <v-stepper-step step="2" :complete="neuralFilmAIProcessingStep > 2">
                    Analyzing image <span class="processing-file-name" v-if="fileName">{{ fileName }}</span>
                </v-stepper-step>

                <v-divider />

                <v-stepper-step step="3" :complete="neuralFilmAIProcessingStep > 3">
                    Calculating AI grades
                </v-stepper-step>
            </v-stepper-header>
        </v-stepper>

        <div ref="container" class="d-flex justify-center"></div>

        <!--		<div class="buttons-continer &#45;&#45;single-item">-->
        <!--			<button type="button" class="modal-btn &#45;&#45;cancel" @click="onCancel">Cancel</button>-->
        <!--		</div>-->
    </div>
</template>

<script>
import * as THREE from 'three';
import { mapState } from 'vuex'

export default {
    data() {
        return {
            container: null,
            renderer: null,
            scene: null,
            group: null,
            camera: null,
            maxParticleCount: 1000,
            particleCount: 300,
            particlePositions: null,
            particlesData: [],
            pointCloud: null,
            linesMesh: null,
            r: 800,
            effectController: {
                showDots: true,
                showLines: true,
                minDistance: 150,
                limitConnections: false,
                maxConnections: 20,
                particleCount: 500
            }
        }
    },

    mounted() {
        this.container = this.$refs.container;
        this.$root.neuralFilmAIProcessingModal = this;

        this.init();
    },

    computed: {
        ...mapState('UI', ['neuralFilmAIProcessingStep', 'neuralFilmAIProcessingModalTitle']),
        ...mapState('Projects', {
            fileName: 'neuralFilmAIProcessingFile'
        })
    },

    methods: {
        onCancel() {
            window.UI.hideModal();
        },

        init() {
            this.camera = new THREE.PerspectiveCamera(45, 1, 1, 4000);
            this.camera.position.z = 1550;

            this.scene = new THREE.Scene();

            this.group = new THREE.Group();
            this.scene.add(this.group);

            const helper = new THREE.BoxHelper(new THREE.Mesh(new THREE.BoxGeometry(this.r, this.r, this.r)));
            helper.material.color.setHex(0x101010);
            helper.material.blending = THREE.AdditiveBlending;
            helper.material.transparent = true;
            this.group.add(helper);

            const segments = this.maxParticleCount * this.maxParticleCount;

            this.positions = new Float32Array(segments * 3);
            this.colors = new Float32Array(segments * 3);

            const pMaterial = new THREE.PointsMaterial({
                color: 0xFFFFFF,
                size: 3,
                blending: THREE.AdditiveBlending,
                transparent: true,
                sizeAttenuation: false
            });

            const particles = new THREE.BufferGeometry();
            this.particlePositions = new Float32Array(this.maxParticleCount * 3);

            for (let i = 0; i < this.maxParticleCount; i++) {

                const x = Math.random() * this.r - this.r / 2;
                const y = Math.random() * this.r - this.r / 2;
                const z = Math.random() * this.r - this.r / 2;

                this.particlePositions[i * 3] = x;
                this.particlePositions[i * 3 + 1] = y;
                this.particlePositions[i * 3 + 2] = z;

                // add it to the geometry
                this.particlesData.push({
                    velocity: new THREE.Vector3(-1 + Math.random() * 2, -1 + Math.random() * 2, -1 + Math.random() * 2),
                    numConnections: 0
                });

            }

            particles.setDrawRange(0, this.particleCount);
            particles.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3).setUsage(THREE.DynamicDrawUsage));

            // create the particle system
            this.pointCloud = new THREE.Points(particles, pMaterial);
            this.group.add(this.pointCloud);

            const geometry = new THREE.BufferGeometry();

            geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
            geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));

            geometry.computeBoundingSphere();

            geometry.setDrawRange(0, 0);

            const material = new THREE.LineBasicMaterial({
                vertexColors: true,
                blending: THREE.AdditiveBlending,
                transparent: true
            });

            this.linesMesh = new THREE.LineSegments(geometry, material);
            this.group.add(this.linesMesh);

            this.renderer = new THREE.WebGLRenderer({antialias: true});
            this.renderer.setPixelRatio(window.devicePixelRatio);
            this.renderer.setSize(500, 500);
            this.renderer.setClearColor(0x1e1e1e);
            // this.renderer.outputColorSpace = THREE.SRGBColorSpace; // default in r152

            this.container.appendChild(this.renderer.domElement);
        },

        startAnimation() {
            this.renderer.setAnimationLoop(this.animate);
        },

        stopAnimation() {
            this.renderer.setAnimationLoop(null);
        },

        animate() {
            let vertexpos = 0;
            let colorpos = 0;
            let numConnected = 0;
            const rHalf = this.r / 2;

            for (let i = 0; i < this.particleCount; i++)
                this.particlesData[i].numConnections = 0;

            for (let i = 0; i < this.particleCount; i++) {

                // get the particle
                const particleData = this.particlesData[i];

                this.particlePositions[i * 3] += particleData.velocity.x;
                this.particlePositions[i * 3 + 1] += particleData.velocity.y;
                this.particlePositions[i * 3 + 2] += particleData.velocity.z;

                if (this.particlePositions[i * 3 + 1] < -rHalf || this.particlePositions[i * 3 + 1] > rHalf)
                    particleData.velocity.y = -particleData.velocity.y;

                if (this.particlePositions[i * 3] < -rHalf || this.particlePositions[i * 3] > rHalf)
                    particleData.velocity.x = -particleData.velocity.x;

                if (this.particlePositions[i * 3 + 2] < -rHalf || this.particlePositions[i * 3 + 2] > rHalf)
                    particleData.velocity.z = -particleData.velocity.z;

                if (this.effectController.limitConnections && particleData.numConnections >= this.effectController.maxConnections)
                    continue;

                // Check collision
                for (let j = i + 1; j < this.particleCount; j++) {

                    const particleDataB = this.particlesData[j];
                    if (this.effectController.limitConnections && particleDataB.numConnections >= this.effectController.maxConnections)
                        continue;

                    const dx = this.particlePositions[i * 3] - this.particlePositions[j * 3];
                    const dy = this.particlePositions[i * 3 + 1] - this.particlePositions[j * 3 + 1];
                    const dz = this.particlePositions[i * 3 + 2] - this.particlePositions[j * 3 + 2];
                    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (dist < this.effectController.minDistance) {

                        particleData.numConnections++;
                        particleDataB.numConnections++;

                        const alpha = 1.0 - dist / this.effectController.minDistance;

                        this.positions[vertexpos++] = this.particlePositions[i * 3];
                        this.positions[vertexpos++] = this.particlePositions[i * 3 + 1];
                        this.positions[vertexpos++] = this.particlePositions[i * 3 + 2];

                        this.positions[vertexpos++] = this.particlePositions[j * 3];
                        this.positions[vertexpos++] = this.particlePositions[j * 3 + 1];
                        this.positions[vertexpos++] = this.particlePositions[j * 3 + 2];

                        this.colors[colorpos++] = alpha;
                        this.colors[colorpos++] = alpha;
                        this.colors[colorpos++] = alpha;

                        this.colors[colorpos++] = alpha;
                        this.colors[colorpos++] = alpha;
                        this.colors[colorpos++] = alpha;

                        numConnected++;

                    }

                }

            }

            this.linesMesh.geometry.setDrawRange(0, numConnected * 2);
            this.linesMesh.geometry.attributes.position.needsUpdate = true;
            this.linesMesh.geometry.attributes.color.needsUpdate = true;

            this.pointCloud.geometry.attributes.position.needsUpdate = true;

            this.render();
        },

        render() {
            const time = Date.now() * 0.001;

            this.group.rotation.y = time * 0.1;
            this.renderer.render(this.scene, this.camera);
        }
    },
};
</script>

<style lang="scss" scoped>
#neural-film-ai-processing-modal {
  width: 60%;
}

::v-deep .v-stepper {
  box-shadow: none;
  background: transparent;

  .v-stepper__step__step {
    position: relative;
  }

  .v-stepper__step__step.primary {
    background-color: var(--color-accent) !important;
  }

  .v-stepper__step--active .v-stepper__step__step {
    transform: scale(1.5);
  }

  .v-stepper__step--inactive .v-stepper__step__step {
    background: transparent !important;
  }

  .v-stepper__label {
    text-align: center;
  }

  .processing-file-name {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    color: #818181;
    line-height: 1.2;
    max-width: 200px;
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>